<template>
  <div class="store-dashboard-page w-full">
    <div class="store-dashboard">
      <section class="dashboard-command">
        <div class="dashboard-command__copy">
          <div class="dashboard-command__header">
            <span class="dashboard-command__icon">
              <hs-icon name="activity" :size="18" />
            </span>
            <div>
              <p class="dashboard-command__eyebrow">Store overview</p>
              <h1>{{ dynamicTitle }}</h1>
            </div>
          </div>
          <p>Track store performance and jump into the workflows that need attention.</p>
          <div class="dashboard-command__actions">
            <hs-button class="dashboard-command__btn dashboard-command__btn--catalog" variant="secondary" icon="package"
              @click="navigateToDashboardArea('catalog')">
              Review catalog
            </hs-button>
            <hs-button class="dashboard-command__btn dashboard-command__btn--orders" variant="secondary" icon="shopping-bag"
              @click="navigateToDashboardArea('orders')">
              Check orders
            </hs-button>
            <div class="dashboard-period">
              <hs-button class="dashboard-command__btn dashboard-command__btn--period" variant="secondary" icon="calendar" @click="handleDateFilter">
                Period
              </hs-button>
              <div v-if="showDateFilter" class="dashboard-period__panel">
                <div class="dashboard-period__panel-header">
                  <div>
                    <strong>Date range</strong>
                    <span>{{ dynamicTitle }}</span>
                  </div>
                  <button type="button" class="dashboard-period__close" @click="closeDateFilter" aria-label="Close date range picker">
                    <hs-icon name="x" :size="15" />
                  </button>
                </div>
                <hs-date-range-picker
                  class="dashboard-period__picker"
                  :model-value="{ start: startDate, end: endDate }"
                  @change="handleDateRangeFilter"
                />
              </div>
            </div>
            <hs-button class="dashboard-command__btn dashboard-command__btn--share" variant="secondary" icon="share-2" @click="handleShareDashboardModal">
              Share view
            </hs-button>
            <hs-button class="dashboard-command__btn dashboard-command__btn--refresh" variant="secondary" icon="refresh-cw" @click="refreshDashboardData">
              Refresh
            </hs-button>
          </div>
        </div>

        <div class="dashboard-command__insights">
          <div v-for="(insight, index) in operationalHighlights" :key="insight.label" class="dashboard-command__insight"
            :style="{ animationDelay: (index * 0.05) + 's' }">
            <span :class="['dashboard-command__insight-icon', insight.tone]">
              <hs-icon :name="insight.icon" :size="17" />
            </span>
            <div>
              <strong>{{ insight.value }}</strong>
              <span>{{ insight.label }}</span>
            </div>
          </div>
        </div>
      </section>

      <div class="dashboard-section-heading">
        <div>
          <p class="dashboard-section-heading__kicker">Key metrics</p>
          <h2>Performance snapshot</h2>
        </div>
        <div class="dashboard-section-heading__actions">
          <span v-if="lastUpdatedLabel" class="dashboard-section-heading__updated">{{ lastUpdatedLabel }}</span>
          <hs-badge tone="success" icon="activity">Live data</hs-badge>
        </div>
      </div>

      <div :class="[
        'store-statscards',
        { 'store-statscards--no-data': statsCardItems.length === 0 },
      ]">
        <div
          class="dashboard-metric-placeholders"
          v-if="isLoadingAmountCharts || isLoadingCountCharts || loadingStats"
        >
          <div v-for="n in 4" :key="n" class="dashboard-metric-placeholder">
            <div class="dashboard-metric-placeholder__top">
              <span></span>
              <i></i>
            </div>
            <strong></strong>
            <p></p>
          </div>
        </div>
        <!-- No Data Message -->
        <div v-else-if="statsCardItems.length === 0" class="col-span-full w-full">
          <empty-state icon="bar-chart-2" title="No Data Available"
            :description="`There is no dashboard data for ${dynamicTitle}.`" :show-suggestion="true"
            suggestion="Try selecting a different date range or check back later." />
        </div>

        <button
          v-else
          v-for="(item, idx) in dashboardMetricCards"
          :key="item.key || item.iconType"
          :class="['dashboard-metric-card', `dashboard-metric-card--${item.tone}`]"
          :style="{ animationDelay: (idx * 0.04) + 's' }"
          type="button"
          @click="navigateToMetric(item)"
        >
          <div class="dashboard-metric-card__header">
            <div class="dashboard-metric-card__title">
              <span class="dashboard-metric-card__icon">
                <hs-icon :name="item.iconType" :size="17" />
              </span>
              <span>{{ item.statsDescription }}</span>
              <hs-tooltip
                v-if="item.profitabilityTooltip"
                :text="item.profitabilityTooltip"
                placement="bottom"
              >
                <span
                  class="dashboard-metric-card__info"
                  role="img"
                  tabindex="0"
                  :aria-label="`${item.statsDescription} definition: ${item.profitabilityTooltip}`"
                  @click.stop
                  @keydown.stop
                >
                  <hs-icon name="info" :size="13" />
                </span>
              </hs-tooltip>
            </div>
            <span class="dashboard-metric-card__state">{{ item.analysisState }}</span>
          </div>
          <strong :title="String(item.statsNumber || '--')">{{ item.formattedNumber }}</strong>
          <div class="dashboard-metric-card__analysis">
            <span>{{ item.metricMeta }}</span>
            <hs-icon class="dashboard-metric-card__arrow" name="arrow-right" :size="13" />
            <hs-tooltip v-if="item.descriptionTooltip" :text="item.descriptionTooltip">
              <span class="dashboard-metric-card__warning">
                <hs-icon name="alert-triangle" :size="13" />
              </span>
            </hs-tooltip>
          </div>
        </button>
      </div>

      <div class="dashboard-section-heading dashboard-section-heading--charts">
        <div>
          <p class="dashboard-section-heading__kicker">Analytics</p>
          <h2>Trend analysis</h2>
        </div>
        <span class="dashboard-section-heading__range">{{ dynamicTitle }}</span>
      </div>

      <div class="store-charts">
        <div class="store-charts__left">
          <div class="dashboard-chart-skeleton" v-if="isLoadingAmountCharts">
            <div class="dashboard-chart-skeleton__header">
              <span></span>
              <i></i>
            </div>
            <div class="dashboard-chart-skeleton__plot">
              <span v-for="n in 6" :key="`amount-grid-${n}`"></span>
              <svg viewBox="0 0 420 160" preserveAspectRatio="none" aria-hidden="true">
                <path d="M0 112 C60 72 88 118 138 82 C188 46 222 68 270 54 C322 38 352 64 420 28" />
              </svg>
            </div>
          </div>
          <hs-chart v-else :title="amountDynamicTitle" :dashboardTypes="getSelectOptions(dashboardTypesAmount)"
            :value="selectedAmountType" :intervalValue="selectedAmountInterval"
            @update:selected-type="handleSelectedAmountType" :loadingOptions="isLoadingStoreDashboardTypes"
            :chartData="amountChartData" @update:selected-interval="handleSelectedAmountInterval" />
        </div>
        <div class="store-charts__right">
          <div class="dashboard-chart-skeleton" v-if="isLoadingCountCharts">
            <div class="dashboard-chart-skeleton__header">
              <span></span>
              <i></i>
            </div>
            <div class="dashboard-chart-skeleton__plot">
              <span v-for="n in 6" :key="`count-grid-${n}`"></span>
              <svg viewBox="0 0 420 160" preserveAspectRatio="none" aria-hidden="true">
                <path d="M0 98 C48 78 82 84 128 70 C174 56 210 98 258 76 C316 50 356 74 420 42" />
              </svg>
            </div>
          </div>
          <hs-chart v-else :title="countDynamicTitle" :colors="['#1D8348']"
            :dashboardTypes="getSelectOptions(dashboardTypesCount)" :value="selectedCountType"
            :intervalValue="selectedCountInterval"
            @update:selected-type="handleSelectedCountType" :loadingOptions="isLoadingStoreDashboardTypes"
            :chartData="countChartData" @update:selected-interval="handleSelectedCountInterval" />
        </div>
      </div>
    </div>
    <div>
      <UpdateStore v-if="showModal" @close-modal="handleModalClose" />
      <update-profile v-if="showProfileModal" @close-modal="handleProfileModalClose" />
      <share-dashboard v-if="showShareDashboardModal" :isDark="isDark" @close-modal="handleShareDashboardModalClose" />
    </div>
  </div>
</template>

<script>
import { useToggle, useDark } from "@vueuse/core";
// import StatsSkeleton from "@/skeletons/StatsSkeleton";
import { format, formatDistanceToNowStrict } from "date-fns";
import { toast } from "vue3-toastify";
import UpdateStore from "@/components/modals/store/UpdateStore";
import UpdateProfile from "@/components/modals/profile/UpdateProfile.vue";
import ShareDashboard from "@/components/modals/store/ShareDashboard";
import EmptyState from "@/components/common/EmptyState.vue";
import {
  canRenderMetric,
  canRenderProfitability,
  profitabilityDefinition,
} from "@/helper/profitability";
import { HsBadge, HsButton, HsDateRangePicker, HsIcon, HsTooltip } from "@/components/ui";

export default {
  name: "DashboardView",
  components: {
    // StatsSkeleton,
    UpdateStore,
    ShareDashboard,
    UpdateProfile,
    EmptyState,
    HsBadge,
    HsButton,
    HsDateRangePicker,
    HsIcon,
    HsTooltip,
  },

  setup() {
    const isDark = useDark();
    const toggleTheme = useToggle(isDark);
    const toggle = () => {
      useToggle(isDark);
      toggleTheme();
    };

    return {
      isDark: isDark,
      toggleTheme,
      toggle,
    };
  },

  data() {
    return {
      statsCardItems: [],
      showModal: false,
      startDate: new Date(new Date() - new Date(30 * 24 * 60 * 60 * 1000)),
      endDate: new Date(),
      dynamicTitle: "Last 30 days", //Transactions from 30 days ago to today
      stores: ["Hovastore"],
      selectedInterval: "daily",
      dashboardTypes: ["sales_amount", "sales_count", "purchase_amount"],
      selectedType: "",
      dashboardTypesCount: [],
      dashboardTypesAmount: [],
      selectedCountType: "",
      selectedAmountType: "",
      amountChartData: [],
      countChartData: [],
      isLoadingCharts: false,
      isLoadingAmountCharts: false,
      isLoadingCountCharts: false,
      selectedAmountInterval: "daily",
      selectedCountInterval: "daily",
      countDynamicTitle: "",
      amountDynamicTitle: "",
      stockValuationStale: false,
      stockValuationCachedAt: null,
      showDateFilter: false,
      showProfileModal: false,
      showShareDashboardModal: false,
      lastUpdatedAt: null,
      menuItems: [
        { label: "Dashboard", route: "dashboard" },
        { label: "Promo codes", route: "promo-codes" },
      ],
      activeMenuItem: 0,
    };
  },
  computed: {
    selectedStore() {
      return this.$store.state.dashboard?.selectedStore;
    },
    currentStore() {
      const selectedId =
        typeof this.selectedStore === "object"
          ? this.selectedStore?.id
          : this.selectedStore;
      return (this.$store.getters.getUserStores || []).find(
        (store) => String(store.id) === String(selectedId),
      ) || (typeof this.selectedStore === "object" ? this.selectedStore : {});
    },
    loadingStats() {
      return this.$store.getters.isLoadingStats;
    },
    isLoadingStores() {
      return this.$store.getters.isLoadingStores;
    },
    isLoadingStoreDashboardTypes() {
      return this.$store.getters.isLoadingStoreDashboardTypes;
    },
    isLoadingStoreTransactionCountData() {
      return this.$store.getters.isLoadingStoreTransactionCountData;
    },
    operationalHighlights() {
      const items = this.statsCardItems || [];
      const findMetric = (keywords) =>
        items.find((item) => {
          const label = String(item.statsDescription || "").toLowerCase();
          return keywords.some((keyword) => label.includes(keyword));
        });

      const expenses = findMetric(["expense"]);
      const invoices = findMetric(["invoice"]);

      return [
        {
          icon: "credit-card",
          tone: "is-amber",
          value: expenses?.statsNumber || "--",
          label: expenses?.statsDescription || "Expense control",
        },
        {
          icon: "file-text",
          tone: "is-green",
          value: invoices?.statsNumber || "--",
          label: invoices?.statsDescription || "Invoice momentum",
        },
      ];
    },
    dashboardMetricCards() {
      return (this.statsCardItems || []).map((item) => ({
        ...item,
        formattedNumber: this.formatDashboardMetric(item.statsNumber),
        metricMeta: item.isStale ? "Refresh recommended" : this.dynamicTitle,
      }));
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return "";
      return `Updated ${formatDistanceToNowStrict(this.lastUpdatedAt, { addSuffix: true })}`;
    },
  },
  watch: {
    $route: {
      handler(newRoute) {
        this.handleRouteChange(newRoute);
      },
      immediate: true,
    },
    selectedStore: {
      handler(newStoreId, oldStoreId) {
        if (newStoreId && newStoreId !== oldStoreId) {
          console.log("StoreDashboardView: Store changed to:", newStoreId);
          this.refreshDashboardData();
        }
      },
      immediate: false,
    },
  },
  methods: {
    isStockValuationUnavailable(statKey, response = {}) {
      const valuationKeys = ["total_stock_cost", "total_stock_selling_value"];
      if (!valuationKeys.includes(statKey)) {
        return false;
      }

      const value = response[statKey];
      return (
        this.stockValuationStale ||
        this.stockValuationCachedAt == null ||
        value === null ||
        value === undefined ||
        Number.isNaN(parseFloat(value))
      );
    },
    handleRouteChange(newRoute) {
      // Extract store ID from route params
      const routeStoreId = newRoute.params?.storeId;

      if (routeStoreId) {
        const currentStoreId = this.$store.state.dashboard?.selectedStore;

        // Only update if the route store ID is different from current selection
        if (String(routeStoreId) !== String(currentStoreId)) {
          // Verify the store exists in the available stores
          const stores = this.$store.getters.getUserStores || [];
          const storeExists = stores.some(
            (store) => String(store.id) === String(routeStoreId),
          );

          if (storeExists) {
            // Update the selected store in the store
            this.$store.commit("SET_SELECTED_STORE", parseInt(routeStoreId));
          }
        }
      }
    },
    handleShowProfileModal() {
      this.showProfileModal = true;
      document.body.style.overflow = "hidden";
    },
    handleShareDashboardModal() {
      this.showShareDashboardModal = true;
      document.body.style.overflow = "hidden";
    },
    handleProfileModalClose() {
      this.showProfileModal = false;
    },
    handleShareDashboardModalClose() {
      this.showShareDashboardModal = false;
    },
    navigateToDashboardArea(area) {
      const routeMap = {
        catalog: "catalog",
        orders: "orders_transactions",
        purchases: "purchases_purchase_orders",
      };
      const routeName = routeMap[area];
      if (!routeName) return;
      const storeId =
        typeof this.selectedStore === "object" && this.selectedStore !== null
          ? this.selectedStore.id
          : this.selectedStore || this.$route.params.storeId;
      this.$router.push({
        name: routeName,
        params: { storeId },
      });
    },
    navigateToMetric(item) {
      const key = String(item?.key || "").toLowerCase();

      if (key.includes("stock")) {
        this.navigateToDashboardArea("catalog");
        return;
      }

      if (key.includes("purchase")) {
        this.navigateToDashboardArea("purchases");
        return;
      }

      if (key.includes("expense")) {
        this.navigateToStoreRoute("store_expenses");
        return;
      }

      if (key.includes("invoice") || key.includes("sales") || key.includes("amount")) {
        this.navigateToStoreRoute("reports");
      }
    },
    navigateToStoreRoute(routeName) {
      const storeId =
        typeof this.selectedStore === "object" && this.selectedStore !== null
          ? this.selectedStore.id
          : this.selectedStore || this.$route.params.storeId;

      this.$router.push({ name: routeName, params: { storeId } });
    },
    closeDateFilter() {
      this.showDateFilter = false;
    },
    handleDateFilter() {
      this.showDateFilter = !this.showDateFilter;
    },
    handleDateRangeFilter(dateRange) {
      this.startDate = dateRange.start;
      this.endDate = dateRange.end;
      this.generateDynamicTitle();
      this.persistSelectedPeriod();
      this.getSelectedStoreStats();
      this.handleStoreSelected(this.selectedAmountType);
      this.handleStoreSelected(this.selectedCountType);
    },
    handleSelectedCountInterval(interval) {
      this.selectedCountInterval = interval;
      this.handleStoreSelected(this.selectedCountType, false, true);
    },
    handleSelectedAmountInterval(interval) {
      this.selectedAmountInterval = interval;
      this.handleStoreSelected(this.selectedAmountType, true, false);
    },
    handleSelectedAmountType(type) {
      this.selectedAmountType = type;
      this.handleStoreSelected(this.selectedAmountType, true, false);
      this.generateDynamicTitle();
    },
    handleSelectedCountType(type) {
      this.selectedCountType = type;
      this.handleStoreSelected(this.selectedCountType, false, true);
      this.generateDynamicTitle();
    },
    capitalizeText(text) {
      return text
        ?.toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    generateDynamicTitle() {
      this.dynamicTitle = this.getPeriodTitle(this.startDate, this.endDate);
      this.countDynamicTitle = `${this.capitalizeText(this.selectedCountType)} · ${this.dynamicTitle}`;
      this.amountDynamicTitle = `${this.capitalizeText(this.selectedAmountType)} · ${this.dynamicTitle}`;
    },
    persistSelectedPeriod() {
      try {
        localStorage.setItem(
          "hovastore-dashboard-period",
          JSON.stringify({
            start: this.startDate?.toISOString?.() || this.startDate,
            end: this.endDate?.toISOString?.() || this.endDate,
          }),
        );
      } catch (error) {
        // Ignore local storage failures.
      }
    },
    hydrateSelectedPeriod() {
      const queryStart = this.$route?.query?.start_date;
      const queryEnd = this.$route?.query?.end_date;
      if (
        /^\d{4}-\d{2}-\d{2}$/.test(queryStart || "") &&
        /^\d{4}-\d{2}-\d{2}$/.test(queryEnd || "")
      ) {
        const start = new Date(`${queryStart}T00:00:00`);
        const end = new Date(`${queryEnd}T23:59:59.999`);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          this.startDate = start;
          this.endDate = end;
          this.generateDynamicTitle();
          return;
        }
      }

      try {
        const storedPeriod = JSON.parse(
          localStorage.getItem("hovastore-dashboard-period") || "null",
        );
        const start = storedPeriod?.start ? new Date(storedPeriod.start) : null;
        const end = storedPeriod?.end ? new Date(storedPeriod.end) : null;

        if (
          start &&
          end &&
          !Number.isNaN(start.getTime()) &&
          !Number.isNaN(end.getTime())
        ) {
          this.startDate = start;
          this.endDate = end;
        }
      } catch (error) {
        // Ignore malformed local storage.
      }

      this.generateDynamicTitle();
    },
    getPeriodTitle(startDate, endDate) {
      const start = this.normalizeDate(startDate);
      const end = this.normalizeDate(endDate);
      const now = new Date();
      const today = this.normalizeDate(now);
      const yesterday = this.addDays(today, -1);
      const currentWeekStart = this.getWeekStart(today);
      const defaultStart = this.addDays(today, -30);
      const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

      if (this.isSameDate(start, today) && this.isSameDate(end, today)) {
        return "Today";
      }

      if (this.isSameDate(start, yesterday) && this.isSameDate(end, yesterday)) {
        return "Yesterday";
      }

      if (this.isSameDate(start, currentMonthStart) && this.isSameDate(end, today)) {
        return "Current month";
      }

      if (this.isSameDate(start, lastMonthStart) && this.isSameDate(end, lastMonthEnd)) {
        return "Last month";
      }

      if (this.isSameDate(start, lastYearStart) && this.isSameDate(end, lastYearEnd)) {
        return "Last year";
      }

      if (this.isSameDate(start, currentWeekStart) && this.isSameDate(end, today)) {
        return "Current week";
      }

      if (this.isSameDate(start, defaultStart) && this.isSameDate(end, today)) {
        return "Last 30 days";
      }

      return `${format(start, "MMM d, yyyy")} to ${format(end, "MMM d, yyyy")}`;
    },
    normalizeDate(value) {
      const date = value instanceof Date ? value : new Date(value);
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    addDays(date, days) {
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + days);
      return nextDate;
    },
    getWeekStart(date) {
      const weekStart = new Date(date);
      const day = weekStart.getDay();
      const daysFromMonday = day === 0 ? 6 : day - 1;
      weekStart.setDate(weekStart.getDate() - daysFromMonday);
      return weekStart;
    },
    isSameDate(firstDate, secondDate) {
      return (
        firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getDate() === secondDate.getDate()
      );
    },
    getOptionsForStore(data) {
      return data.map((item) => ({
        value: item.id,
        name: item.name,
      }));
    },
    getSelectOptions(data) {
      return data.map((item) => ({
        value: item,
        name: this.capitalizeText(item),
      }));
    },
    getDateRangeFormatted() {
      return {
        start: format(this.startDate, "dd/MM/yyyy HH:mm:ss"),
        end: format(this.endDate, "dd/MM/yyyy HH:mm:ss"),
      };
    },
    generateCardItems(response) {
      const normalizedResponse = {
        ...response,
        gross_profit_amount:
          response.gross_profit_amount ??
          response.sales_profit ??
          response.gross_profit,
        profit_after_recorded_expenses:
          response.profit_after_recorded_expenses ?? response.net_profit,
      };
      const keys = Object.keys(normalizedResponse);
      const profitabilityAllowed = canRenderProfitability(
        response.can_view_profitability ?? response.profitability_access,
        this.currentStore,
      );
      const definitions = profitabilityDefinition(
        response.profitability_breakdown ||
          response.profitability_calculation ||
          {},
      );
      const labelOverrides = {
        total_vat: "Total VAT",
        total_invoices: "Total Invoices",
        total_stock_cost: "Total Stock Cost",
        total_stock_selling_value: "Total Stock Selling Value",
        gross_profit_amount: "Gross profit",
        profit_after_recorded_expenses: "Profit after recorded expenses",
      };

      const objects = keys
        .filter((key) => !key.startsWith("stock_valuation_"))
        .filter(
          (key) =>
            ![
              "profitability_access",
              "profitability_calculation",
              "can_view_profitability",
              "profitability_breakdown",
              "sales_profit",
              "gross_profit",
              "net_profit",
              "net_sales_amount",
              "allocated_inventory_cost",
              "recorded_expenses_amount",
            ].includes(key),
        )
        .filter((key) =>
          canRenderMetric(
            key,
            normalizedResponse[key],
            profitabilityAllowed,
          ),
        )
        .filter((key) => {
          // Filter out cards with zero values
          const value = normalizedResponse[key];
          const numericValue = parseFloat(value);
          if (
            key === "total_stock_cost" ||
            key === "total_stock_selling_value"
          ) {
            return (
              this.isStockValuationUnavailable(key, normalizedResponse) ||
              !isNaN(numericValue)
            );
          }
          if (key.includes("profit")) {
            return !isNaN(numericValue);
          }
          return !isNaN(numericValue) && numericValue > 0;
        })
        .map((key) => {
          const metricStyle = this.getMetricStyle(key);
          const isStale = Boolean(this.getStockValuationTooltip(key));

          return {
            key: key,
            iconType: metricStyle.icon,
            statsNumber: this.isStockValuationUnavailable(
              key,
              normalizedResponse,
            )
              ? "--"
              : normalizedResponse[key],
            statsDescription: labelOverrides[key] || this.$capitalizeText(key),
            descriptionTooltip: this.getStockValuationTooltip(key),
            profitabilityTooltip:
              key === "profit_after_recorded_expenses"
                ? definitions.net
                : key === "gross_profit_amount"
                  ? definitions.gross
                  : "",
            tone: metricStyle.tone,
            analysisState: isStale ? "Stale" : "Live",
            isStale,
          };
        })
        .sort(
          (first, second) =>
            this.getMetricPriority(first.key) - this.getMetricPriority(second.key),
        );

      return objects;
    },
    getMetricPriority(key = "") {
      const normalizedKey = String(key).toLowerCase();

      if (normalizedKey.includes("sales") || normalizedKey.includes("amount")) return 10;
      if (normalizedKey.includes("profit")) return 15;
      if (normalizedKey.includes("invoice")) return 20;
      if (normalizedKey.includes("vat") || normalizedKey.includes("tax")) return 30;
      if (normalizedKey.includes("stock")) return 40;
      if (normalizedKey.includes("expense")) return 50;
      if (normalizedKey.includes("purchase")) return 60;
      return 100;
    },
    getMetricStyle(key = "") {
      const normalizedKey = String(key).toLowerCase();

      if (normalizedKey.includes("invoice")) {
        return { icon: "file-text", tone: "success" };
      }

      if (normalizedKey.includes("expense")) {
        return { icon: "credit-card", tone: "warning" };
      }

      if (normalizedKey.includes("stock")) {
        return { icon: "package", tone: "info" };
      }

      if (normalizedKey.includes("vat") || normalizedKey.includes("tax")) {
        return { icon: "percent", tone: "primary" };
      }

      if (normalizedKey.includes("purchase")) {
        return { icon: "shopping-cart", tone: "primary" };
      }

      if (normalizedKey.includes("sales") || normalizedKey.includes("amount")) {
        return { icon: "dollar-sign", tone: "success" };
      }

      if (normalizedKey.includes("profit")) {
        return { icon: "trending-up", tone: "success" };
      }

      return { icon: "bar-chart-2", tone: "neutral" };
    },
    formatDashboardMetric(value) {
      if (!value && value !== 0) {
        return "--";
      }

      const numberStr = String(value);
      const suffixes = ["K", "M", "B", "T"];
      const suffix = suffixes.find((item) => numberStr.endsWith(item));

      if (suffix) {
        return numberStr;
      }

      const num = parseFloat(numberStr);
      if (Number.isNaN(num)) {
        return numberStr;
      }

      if (num >= 1e12) return `${this.formatCompactNumber(num / 1e12)}T`;
      if (num >= 1e9) return `${this.formatCompactNumber(num / 1e9)}B`;
      if (num >= 1e6) return `${this.formatCompactNumber(num / 1e6)}M`;
      if (num >= 1e3) return `${this.formatCompactNumber(num / 1e3)}K`;
      return num.toLocaleString();
    },
    formatCompactNumber(value) {
      const formatted = parseFloat(value.toFixed(1));
      return formatted % 1 === 0 ? formatted.toString() : formatted.toFixed(1);
    },
    formatRelativeTimestamp(timestamp) {
      if (!timestamp) {
        return null;
      }

      const parsedDate = new Date(timestamp);
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return formatDistanceToNowStrict(parsedDate, { addSuffix: true });
    },
    getStockValuationTooltip(statKey) {
      const valuationKeys = ["total_stock_cost", "total_stock_selling_value"];

      if (!valuationKeys.includes(statKey) || !this.stockValuationStale) {
        return "";
      }

      const relativeTimestamp = this.formatRelativeTimestamp(
        this.stockValuationCachedAt,
      );

      if (!relativeTimestamp) {
        return "Data is stale. Refresh time unavailable.";
      }

      return `Data is stale. Last refreshed ${relativeTimestamp}.`;
    },
    getStockValuationBannerMessage() {
      const relativeTimestamp = this.formatRelativeTimestamp(
        this.stockValuationCachedAt,
      );

      if (!relativeTimestamp) {
        return "Stock valuation figures may be up to 24 hours old. Refresh time unavailable.";
      }

      return `Stock valuation figures may be up to 24 hours old. Last refreshed ${relativeTimestamp}.`;
    },
    handleModalClose() {
      this.showModal = false;
    },
    toggleProfileModal() {
      this.showProfileModal = !this.showProfileModal;
    },

    handleUpdateStartDate(newStartDate) {
      this.startDate = newStartDate;
    },
    handleUpdateEndDate(newEndDate) {
      this.endDate = newEndDate;
    },
    capitalizeWords() {
      return this.selectedType
        ?.toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    getFilterData(selectedDashboardType = "") {
      const { start, end } = this.getDateRangeFormatted();
      const intervalParam = selectedDashboardType
        ?.toLowerCase()
        ?.includes("amount")
        ? this.selectedAmountInterval.toLowerCase()
        : this.selectedCountInterval.toLowerCase();
      return {
        start_time: start,
        end_time: end,
        interval: intervalParam,
        dashboard_type: selectedDashboardType,
        selectedStore: this.selectedStore,
      };
    },
    async getSelectedStoreStats() {
      try {
        const filterData = this.getFilterData();
        const statsRes = await this.$store.dispatch(
          "getStoreStats",
          filterData,
        );
        const statsPayload = { ...(statsRes || {}) };

        this.stockValuationStale = statsPayload.stock_valuation_stale === true;
        this.stockValuationCachedAt =
          statsPayload.stock_valuation_cached_at || null;
        delete statsPayload.start_time;
        delete statsPayload.end_time;

        this.statsCardItems = this.generateCardItems(statsPayload);
        this.lastUpdatedAt = new Date();
      } catch (error) {
        this.stockValuationStale = false;
        this.stockValuationCachedAt = null;
        toast.error(
          error?.response?.data?.error?.message ||
          "An error occurred when fetching store statistics",
        );
      }
    },
    async handleStoreSelected(
      selectedType,
      loadAmount = false,
      loadCount = false,
    ) {
      try {
        this.isLoadingAmountCharts = loadCount ? false : true;
        this.isLoadingCountCharts = loadAmount ? false : true;
        const filterData = this.getFilterData(selectedType);

        const responseData = await this.$store.dispatch(
          "getStoreTransactionStats",
          filterData,
        );
        this.isLoadingAmountCharts = false;
        this.isLoadingCountCharts = false;

        if (selectedType.toLowerCase().includes("amount")) {
          this.amountChartData = [responseData];
        } else if (selectedType.toLowerCase().includes("count")) {
          this.countChartData = [responseData];
        }
        this.lastUpdatedAt = new Date();
      } catch (error) {
        this.isLoadingAmountCharts = false;
        this.isLoadingCountCharts = false;
        toast.error(
          error?.response?.data?.error?.message ||
          "An error occurred when fetching store data",
        );
      }
    },
    getDashboardTypes() {
      this.$store
        .dispatch("getStoreDashboardTypes")
        .then((response) => {
          this.dashboardTypes = response || [];
          this.dashboardTypesAmount = this.dashboardTypes.filter((item) =>
            item.toLowerCase().includes("amount"),
          );
          this.dashboardTypesCount = this.dashboardTypes.filter((item) =>
            item.toLowerCase().includes("count"),
          );
          this.selectedAmountType = this.dashboardTypesAmount[0];
          this.selectedCountType = this.dashboardTypesCount[0];
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.error?.message ||
            "An error occurred when fetching dashboard types",
          );
        });
    },
    resolveInitialStoreId(stores = []) {
      if (!stores.length) {
        return null;
      }

      const persistedSelection = this.$store.state.dashboard?.selectedStore;

      if (persistedSelection) {
        const match = stores.find(
          (store) => String(store.id) === String(persistedSelection),
        );

        if (match) {
          return match.id;
        }
      }

      return stores[0]?.id ?? null;
    },
    async refreshDashboardData() {
      try {
        console.log(
          "StoreDashboardView: Refreshing dashboard data for store:",
          this.selectedStore,
        );

        // Refresh stats cards
        await this.getSelectedStoreStats();

        // Refresh charts if types are selected
        if (this.selectedAmountType) {
          await this.handleStoreSelected(this.selectedAmountType, true, false);
        }
        if (this.selectedCountType) {
          await this.handleStoreSelected(this.selectedCountType, false, true);
        }

        // Update dynamic titles
        this.generateDynamicTitle();

        console.log(
          "StoreDashboardView: Dashboard data refreshed successfully",
        );
      } catch (error) {
        console.error(
          "StoreDashboardView: Error refreshing dashboard data:",
          error,
        );
        toast.error("Failed to refresh dashboard data. Please try again.");
      }
    },
  },
  mounted() {
    this.hydrateSelectedPeriod();
    this.getDashboardTypes();
    this.$store
      .dispatch("getAllUsersStore")
      .then(({ stores: fetchedStores = [] }) => {
        this.stores = fetchedStores;
        localStorage.setItem("stores", JSON.stringify(this.stores));

        const initialStoreId = this.resolveInitialStoreId(this.stores);

        if (initialStoreId && this.dashboardTypes.length > 0) {
          this.selectedStore = initialStoreId;
          this.$store.commit("SET_SELECTED_STORE", initialStoreId);
          this.generateDynamicTitle(true);
          this.getSelectedStoreStats();
          if (this.selectedAmountType) {
            this.handleStoreSelected(this.selectedAmountType);
          }
          if (this.selectedCountType) {
            this.handleStoreSelected(this.selectedCountType);
          }
        }
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.error?.message ||
          "An error occurred when fetching stores",
        );
      });
  },
};
</script>
